import { Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Control } from "react-hook-form";
import { Button } from "../../../../../../components/ui/common/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../../../components/ui/common/table";
import { WEIGHT_UNIT_TYPES_OPTIONs_WITH_GRAM } from "../../../../../../config/constants";
import { MEASUREMENT_TYPES } from "../../../../../../config/enums/MeasurementTypes";
import PackagingMaterialFormCells from "./PackagingMaterialFormCells";

interface PackagingMaterialsSectionProps {
  control: Control<any>;
  records: any[];
  packagingTypeData: any[];
  onMaterialTypeChange: (value: number) => void;
  materialWeightUnit: MEASUREMENT_TYPES;
  onMaterialWeightUnitChange: (value: MEASUREMENT_TYPES) => void;
  isEditing: boolean;
  editingId: string | null;
  canSave: boolean;
  lockStatus: boolean;
  sectionLabel?: string;
  onAdd: () => void;
  onUpdate: () => void;
  onEdit: (record: any) => void;
  onDelete: (record: any) => void;
  onClearDraft: () => void;
}

const headerCellClass = "tw-px-4 tw-py-3 tw-align-middle";
const indexCellClass = "tw-px-3 tw-py-3 tw-align-middle";
const cellClass = "tw-px-4 tw-py-3 tw-align-middle";

const getWeightUnitName = (weightUnit: MEASUREMENT_TYPES) => {
  return (
    WEIGHT_UNIT_TYPES_OPTIONs_WITH_GRAM.find(unit => unit.value === weightUnit)?.text ||
    WEIGHT_UNIT_TYPES_OPTIONs_WITH_GRAM[0].text
  );
};

const RequiredHeader = ({ children }: { children: string }) => (
  <span className="tw-font-medium tw-text-muted-foreground">
    {children}
    <span className="tw-text-destructive tw-ml-0.5">*</span>
  </span>
);

const PackagingMaterialsSection = ({
  control,
  records,
  packagingTypeData,
  onMaterialTypeChange,
  materialWeightUnit,
  onMaterialWeightUnitChange,
  isEditing,
  editingId,
  canSave,
  lockStatus,
  sectionLabel = "PACKAGING MATERIALS",
  onAdd,
  onUpdate,
  onEdit,
  onDelete,
  onClearDraft
}: PackagingMaterialsSectionProps) => {
  const [draftVisible, setDraftVisible] = useState(false);
  const draftRowIndex = records?.length ? records.length + 1 : 1;
  const showDraftRow = !lockStatus && !isEditing && draftVisible;
  const showTable = (records?.length ?? 0) > 0 || draftVisible;

  useEffect(() => {
    setDraftVisible(false);
  }, [records?.length]);

  const handleAddMaterial = () => {
    onClearDraft();
    setDraftVisible(true);
  };

  const handleClearDraft = () => {
    onClearDraft();
    setDraftVisible(false);
  };

  const formCellProps = {
    control,
    packagingTypeData,
    onMaterialTypeChange,
    materialWeightUnit,
    onMaterialWeightUnitChange,
    lockStatus,
    canSave
  };

  return (
    <div className="tw-space-y-3 tw-pt-1">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
          {sectionLabel}
        </p>
        {!lockStatus && (
          <Button
            type="button"
            size="sm"
            className="tw-gap-1 tw-h-8 tw-px-3 tw-text-xs tw-font-medium"
            onClick={handleAddMaterial}
          >
            <Plus className="tw-h-3.5 tw-w-3.5" />
            Add Material
          </Button>
        )}
      </div>

      {!showTable ? (
        <p className="tw-text-xs tw-text-muted-foreground tw-italic tw-py-1">
          No materials added yet. Click &apos;Add Material&apos; to start.
        </p>
      ) : (
        <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-overflow-hidden">
          <Table className="tw-table-fixed">
            <TableHeader>
              <TableRow className="tw-bg-muted/40 hover:tw-bg-muted/40 tw-border-b">
                <TableHead className={`${indexCellClass} tw-w-4 tw-text-[11px]`}>#</TableHead>
                <TableHead className={`${headerCellClass} tw-text-[11px] tw-w-[22%]`}>
                  <RequiredHeader>Packaging Name</RequiredHeader>
                </TableHead>
                <TableHead className={`${headerCellClass} tw-text-[11px] tw-w-[24%]`}>
                  <RequiredHeader>Packaging Material</RequiredHeader>
                </TableHead>
                <TableHead className={`${headerCellClass} tw-text-[11px] tw-w-[24%]`}>
                  <RequiredHeader>Material Weight</RequiredHeader>
                </TableHead>
                <TableHead className={`${headerCellClass} tw-text-[11px] tw-w-[14%]`}>
                  Material Cost
                </TableHead>
                <TableHead className={`${headerCellClass} tw-text-[11px] tw-w-16 tw-text-right`}>
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records?.map((record, index) => {
                const material = packagingTypeData?.find(
                  (e: any) => e.value === record?.packageMaterial
                );
                const isRowEditing = isEditing && record._id === editingId;

                if (isRowEditing) {
                  return (
                    <TableRow
                      key={record._id ?? index}
                      className="tw-bg-muted/10 hover:tw-bg-muted/10"
                    >
                      <TableCell
                        className={`${indexCellClass} tw-text-xs tw-font-medium tw-text-muted-foreground`}
                      >
                        {index + 1}
                      </TableCell>
                      <PackagingMaterialFormCells
                        {...formCellProps}
                        onSave={onUpdate}
                        onClear={handleClearDraft}
                      />
                    </TableRow>
                  );
                }

                return (
                  <TableRow key={record._id ?? index} className="hover:tw-bg-muted/20">
                    <TableCell
                      className={`${indexCellClass} tw-text-xs tw-font-medium tw-text-muted-foreground`}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell className={`${cellClass} tw-text-xs tw-font-medium tw-truncate`}>
                      {record?.packageName ?? "—"}
                    </TableCell>
                    <TableCell
                      className={`${cellClass} tw-text-xs tw-text-muted-foreground tw-truncate`}
                    >
                      {material?.text || "—"}
                    </TableCell>
                    <TableCell
                      className={`${cellClass} tw-text-xs tw-font-mono tw-text-muted-foreground`}
                    >
                      {record?.materialWeight ?? "—"}{" "}
                      {getWeightUnitName(Number(record?.weightUnit))}
                    </TableCell>
                    <TableCell
                      className={`${cellClass} tw-text-xs tw-font-mono tw-text-muted-foreground`}
                    >
                      {record?.materialCost != null && record?.materialCost !== ""
                        ? record.materialCost
                        : "—"}
                    </TableCell>
                    <TableCell className={cellClass}>
                      {!lockStatus && (
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-0.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="tw-h-7 tw-w-7 tw-p-0"
                            onClick={() => onEdit(record)}
                          >
                            <Pencil className="tw-h-3.5 tw-w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="tw-h-7 tw-w-7 tw-p-0 tw-text-destructive hover:tw-text-destructive"
                            onClick={() => onDelete(record)}
                          >
                            <Trash2 className="tw-h-3.5 tw-w-3.5" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}

              {showDraftRow && (
                <TableRow className="tw-bg-muted/10 hover:tw-bg-muted/10">
                  <TableCell
                    className={`${indexCellClass} tw-text-xs tw-font-medium tw-text-muted-foreground`}
                  >
                    {draftRowIndex}
                  </TableCell>
                  <PackagingMaterialFormCells
                    {...formCellProps}
                    onSave={onAdd}
                    onClear={handleClearDraft}
                  />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default PackagingMaterialsSection;
